/**
 * Onboarding & Access — one place for everything about getting a tenant's
 * administrator into their workspace.
 *
 * These controls were scattered: readiness on one tab, the user list on
 * another, nothing at all for the invitation. An operator answering "has this
 * customer been contacted, and can they get in?" had to visit three surfaces
 * and infer the answer from an email log.
 *
 * Invitation state is CANONICAL and comes from the backend. It is not derived
 * here, and it is not read from `email_logs` — that log records that a message
 * was generated, not whether this person has been invited, whether they
 * accepted, or whether the credential they hold is still the current one.
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../../components/ui/dialog';
import {
  AlertTriangle, Check, Copy, KeyRound, Loader2, Mail, RefreshCw, UserCheck,
} from 'lucide-react';
// `Rocket` used to sit in the header. It is gone rather than kept "in case":
// the header now carries real identity artwork, and a flat glyph beside a
// dimensional mark is the one place this system says two different things
// about the same subject.
import toast from 'react-hot-toast';
import ImpersonationReasonDialog from './ImpersonationReasonDialog';
import { describeProgress } from './provisioningProgress';
import StatusBadge from '../../../components/admin/StatusBadge';
import IdentityMark from '../../../design-system/identity/IdentityMark';
import {
  describeInvitation, describeReadiness, describeSampleCount, describeSampleData, isSampleRepairable,
} from './onboardingPresentation';

const API = process.env.REACT_APP_BACKEND_URL;
const CONTROL_PLANE_API = `${process.env.REACT_APP_BACKEND_URL}/api/admin/control-plane`;

/* The three tone maps that used to sit here are gone. They were sixteen
   hardcoded Tailwind swatch pairs — a fourth visual language on a page whose
   other status chips all route through `StatusBadge`, and light-only, so eight
   of them measured between 2.57:1 and 3.83:1 against the dark card and two
   were already under 4.5:1 in the LIGHT theme. The vocabulary, the words and
   the reasoning now live in `onboardingPresentation.js`, which is pure and
   therefore testable; the tones are `StatusBadge`'s, which is the point of
   having one. */

const fmt = (v) => (v ? new Date(v).toLocaleString() : null);

const OnboardingPanel = ({ tenantId, tenantName, headers, can }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(null);
  const [confirmSend, setConfirmSend] = useState(false);
  const [tempPassword, setTempPassword] = useState(null);
  const [copied, setCopied] = useState(false);
  // Non-null opens the reason dialog for View As; the mint and the redirect
  // are startViewAs's, behind it.
  const [viewAsTarget, setViewAsTarget] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: d } = await axios.get(`${API}/api/admin/tenants/${tenantId}/onboarding`, { headers });
      setData(d);
    } catch (e) {
      // A failed load must not render as "never sent" — that is an answer.
      setError(e.response?.status === 403
        ? 'You do not have permission to view onboarding state.'
        : 'Onboarding state could not be loaded.');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [tenantId, headers]);

  useEffect(() => { load(); }, [load]);

  const invitation = data?.invitation || {};
  const state = invitation.state || 'never_sent';
  const readiness = data?.readiness?.state || 'unknown';
  const sample = data?.sample_data || {};

  // One description per axis, from the one module that holds the vocabulary.
  // Each carries `known`, so a state from a newer Platform is shown as itself
  // and SAID to be unrecognised rather than mapped onto a lookalike.
  const invite = describeInvitation(state);
  const ready = describeReadiness(readiness);
  const seed = describeSampleData(sample.state);
  const seedCount = describeSampleCount(sample);

  const sendInvitation = async () => {
    setBusy('invitation');
    try {
      const { data: r } = await axios.post(
        `${API}/api/admin/tenants/${tenantId}/invitation/send`, {}, { headers });
      if (r.sent) {
        toast.success(r.resend
          ? 'A new invitation was sent. The previous one no longer works.'
          : 'Invitation sent.');
      } else {
        // Two truths, separately. The credential exists even though delivery
        // failed, so this is recoverable without another send.
        toast.error(`The invitation could not be delivered: ${r.delivery_error || 'unknown reason'}`);
      }
      setConfirmSend(false);
      await load();
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(detail || 'The invitation could not be sent.');
      if (e.response?.status === 409) setConfirmSend(false);
    } finally {
      setBusy(null);
    }
  };

  const generateTemporaryPassword = async (userId) => {
    setBusy('password');
    try {
      const { data: r } = await axios.post(
        `${API}/api/admin/users/${userId}/temporary-password`, {}, { headers });
      setTempPassword(r);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'A temporary password could not be generated.');
    } finally {
      setBusy(null);
    }
  };

  /*
   * REPAIR, NOT RETRY — and the route this used to call does not exist.
   *
   * It posted to `/provisioning/retry`, which is defined nowhere. Measured on
   * the serving build with two positive controls, so it is not an auth
   * artefact:
   *
   *     POST .../provisioning/retry      404   route absent
   *     POST .../provisioning/repair     401   route exists, auth required
   *     POST .../provisioning/readiness  405   route exists, wrong method
   *
   * So this button has been dead in production: every press produced "The
   * retry could not be started." and nothing ran.
   *
   * `/provisioning/repair` is also the CORRECT destination, not merely an
   * available one. `retry_count` is a monotonic count of attempts consumed
   * against one job row and is never reset — a fresh budget is obtained by
   * minting a NEW row, which is exactly what repair does while reusing the
   * frozen spec's provisioning_request_id so the platform converges on the
   * same claim. The absent `/retry` route, had it existed and called
   * `retry_job`, would have re-queued a row whose budget was by definition
   * exhausted and bought exactly one attempt.
   *
   * 409 is an ordinary answer here (a job already running, or no frozen spec),
   * so its detail is surfaced rather than flattened into a generic failure.
   */
  const repairProvisioning = async () => {
    setBusy('sample');
    try {
      await axios.post(`${API}/api/admin/tenants/${tenantId}/provisioning/repair`, {}, { headers });
      toast.success('Repair started.');
      await load();
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(
        (typeof detail === 'object' ? detail?.message : detail)
        || 'The repair could not be started.',
      );
    } finally {
      setBusy(null);
    }
  };

  /**
   * One fact, and the sentence saying what it is a fact ABOUT.
   *
   * The `meaning` line is not decoration. Four states sit in a column four rows
   * tall and they are facts about four different subjects — a runtime, an
   * email, a person's account and a batch of records — and "Ready", "Accepted"
   * and "In place" read as one verdict to anybody given only the three words.
   *
   * It stacks under the label on a phone and sits beside it from `sm`: at
   * 375px a two-column row puts a chip and a sentence in about 160px each,
   * which breaks the sentence into a ladder of single words.
   */
  const Row = ({ label, meaning, children, testId }) => (
    <div
      className="grid grid-cols-1 gap-x-4 gap-y-1 py-2.5 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-start"
      data-testid={testId}
    >
      <div className="min-w-0">
        <p className="text-sm text-foreground">{label}</p>
        {meaning && (
          <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">{meaning}</p>
        )}
      </div>
      <div className="min-w-0 text-sm sm:text-right">{children}</div>
    </div>
  );

  return (
    <Card data-testid="onboarding-panel">
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex min-w-0 items-start gap-3">
            {/* THE ONE PIECE OF IDENTITY ARTWORK ON THIS CARD. Onboarding is
                about getting a PERSON into a workspace, so it reuses the
                operator archetype — a figure carrying a key notch — rather
                than commissioning a ninth silhouette for a subject the system
                already draws. The Rocket glyph it replaced was flat, which was
                correct for a glyph, but it was also the only mark on a panel
                that is the emotional high point of the whole Create Tenant
                flow: the moment the customer can actually get in. */}
            <IdentityMark
              archetype="operator"
              ramp="indigo"
              size="lg"
              className="mt-0.5 shrink-0"
            />
            <div className="min-w-0">
              <CardTitle className="text-base">Onboarding &amp; Access</CardTitle>
              <p className="text-xs text-muted-foreground">
                Whether this customer has been contacted, and whether they can get in.
              </p>
            </div>
          </div>
          {/* An ACTION. Flat, monochrome, one stroke weight — never artwork. */}
          <Button
            variant="ghost"
            size="sm"
            onClick={load}
            disabled={loading}
            data-testid="onboarding-refresh"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} aria-hidden="true" />
            <span className="sr-only">Re-check onboarding state</span>
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          /* LOADING SAYS WHAT IT IS DOING. A bare spinner in an empty card is
             indistinguishable from a card that has nothing in it, which is the
             one reading this panel must never produce. */
          <div
            className="flex items-center justify-center gap-2 py-6 text-sm text-muted-foreground"
            data-testid="onboarding-loading"
          >
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            Reading onboarding state…
          </div>
        ) : error ? (
          /* A FAILED READ IS ITS OWN STATE, drawn as a banner rather than as a
             grey sentence, and it says what is NOT being claimed. The previous
             treatment put the failure in the same faint ink as a caption, so a
             panel that could not answer looked like a panel answering quietly.
             The tone is `warning`, not `destructive`: nothing failed for the
             tenant — Cluvik failed to look. */
          <div
            className="flex items-start gap-3 rounded-xl border border-warning/40 bg-warning/10 px-4 py-3"
            data-testid="onboarding-error"
            role="alert"
          >
            <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-warning-text" aria-hidden="true" />
            <div className="min-w-0">
              <p className="text-sm font-medium text-foreground">{error}</p>
              <p className="mt-1 text-xs text-muted-foreground">
                Nothing below is being claimed about this tenant. This is a failed read,
                not a report that the tenant has no invitation and no seed.
              </p>
            </div>
          </div>
        ) : (
          <>
            <div className="divide-y divide-border" data-testid="onboarding-facts">
              <Row
                label="Runtime readiness"
                meaning={ready.meaning}
                testId="onboarding-readiness"
              >
                <StatusBadge status={ready.badge} label={ready.label} />
                {/* Same projection the header and the Provisioning tab read.
                    This panel previously showed only the readiness word, so it
                    could report Ready while the header said PROVISIONING.
                    Absent once the tenant stops provisioning. */}
                {describeProgress(data?.readiness?.progress) && (
                  <p
                    className="mt-1 text-xs text-muted-foreground"
                    data-testid="onboarding-progress"
                  >
                    {describeProgress(data.readiness.progress)}
                  </p>
                )}
                {!ready.known && (
                  <p className="mt-1 text-xs text-warning-text" data-testid="onboarding-readiness-unknown-state">
                    This build does not know this readiness state.
                  </p>
                )}
              </Row>

              <Row
                label="Primary administrator"
                meaning="The one account the invitation and View As both act on."
                testId="onboarding-admin"
              >
                {invitation.recipient
                  ? <span className="break-all text-foreground">{invitation.recipient}</span>
                  /* AN ABSENCE, AND IT IS LOAD BEARING. This was `text-slate-400`
                     at 2.56:1 in the LIGHT theme — the faintest ink on the panel
                     carrying the fact that there is nobody to invite. */
                  : <span className="text-muted-foreground" data-testid="onboarding-no-admin">
                      No administrator on this tenant
                    </span>}
              </Row>

              <Row
                label="Invitation"
                meaning={invite.meaning}
                testId="onboarding-invitation"
              >
                <StatusBadge status={invite.badge} label={invite.label} />
                {invitation.last_sent_at && (
                  <span className="mt-1 block text-xs text-muted-foreground">
                    Last sent {fmt(invitation.last_sent_at)}
                    {invitation.sent_count > 1 && ` · ${invitation.sent_count} times`}
                  </span>
                )}
                {invitation.last_delivery_error && (
                  <span
                    className="mt-1 block break-words text-xs text-destructive-text"
                    data-testid="onboarding-delivery-error"
                  >
                    {invitation.last_delivery_error}
                  </span>
                )}
                {!invite.known && (
                  <p className="mt-1 text-xs text-warning-text">
                    This build does not know this invitation state.
                  </p>
                )}
              </Row>

              <Row
                label="Sample data"
                meaning={seed.meaning}
                testId="onboarding-sample"
              >
                <StatusBadge status={seed.badge} label={seed.label} />
                {/* Rows with their unit — never the number of object types
                    dressed as a record count. See describeSampleCount. */}
                {seedCount && (
                  <span className="mt-1 block text-xs text-muted-foreground" data-testid="onboarding-sample-count">
                    {seedCount}
                  </span>
                )}
                {!seed.known && (
                  <p className="mt-1 text-xs text-warning-text" data-testid="onboarding-sample-unknown-state">
                    This build does not know this sample-data state. It is shown as the
                    Platform named it rather than mapped onto one that looks similar.
                  </p>
                )}
              </Row>
            </div>

            {/* Why a requested seed did not run. This is NOT the same shape as
                the invitation hold, and the difference is the point: with no
                batch document the Sample data row above reads "Not requested",
                which on the vendored provisioning path is the opposite of what
                happened -- the operator asked, and no seeder existed to run it.
                Rendered immediately under that row so the correction sits with
                the claim it corrects.

                Gated on the row still reading `not_requested`, so a seed that
                later succeeds retires this notice without anyone having to
                unset the stored field. A hold and a completed seed must never
                render together; the reader should not have to reconcile two
                statements about one thing. */}
            {data?.sample_data_hold_reason && sample.state === 'not_requested' && (
              <div
                className="mt-3 rounded-xl border border-warning/40 bg-warning/10 px-4 py-3 text-xs text-foreground"
                data-testid="onboarding-sample-data-held"
              >
                <strong>Sample data held.</strong> {data.sample_data_hold_reason}
              </div>
            )}

            {/* Why an automatic invitation did not go out. Held is not failed,
                and the operator should not have to guess which. */}
            {data?.invitation_hold_reason && (
              <div
                className="mt-3 rounded-xl border border-warning/40 bg-warning/10 px-4 py-3 text-xs text-foreground"
                data-testid="onboarding-invitation-held"
              >
                <strong>Invitation held.</strong> {data.invitation_hold_reason}. The tenant itself
                is unaffected — send it manually once you are satisfied.
              </div>
            )}

            <div className="mt-4 flex flex-wrap gap-2">
              {state !== 'accepted' && can('user:update') && (
                <Button size="sm" onClick={() => setConfirmSend(true)}
                        disabled={busy === 'invitation'} data-testid="send-invitation-btn">
                  <Mail className="h-4 w-4 mr-2" />
                  {state === 'never_sent' ? 'Send Invitation' : 'Resend Invitation'}
                </Button>
              )}

              {/* View As first, and worded as the ordinary path. An operator
                  should not learn that changing a customer's password is how
                  you inspect their tenant. */}
              {can('tenant:impersonate') && (
                <Button size="sm" variant="outline" data-testid="view-as-btn"
                        onClick={() => {
                          // This button once dispatched a `cluvik:view-as`
                          // CustomEvent that nothing listened for. It now
                          // opens the shared reason dialog; the tab is opened
                          // in that dialog's confirm click, and the mint and
                          // redirect are startViewAs's.
                          setViewAsTarget({ tenantId, tenantName });
                        }}>
                  <UserCheck className="h-4 w-4 mr-2" />
                  View As User
                </Button>
              )}

              {can('user:reset_password') && data?.primary_admin_id && (
                <Button size="sm" variant="outline"
                        onClick={() => generateTemporaryPassword(data.primary_admin_id)}
                        disabled={busy === 'password'} data-testid="temp-password-btn">
                  <KeyRound className="h-4 w-4 mr-2" />
                  Generate Temporary Password
                </Button>
              )}

              {isSampleRepairable(sample.state) && can('provisioning:execute') && (
                <Button size="sm" variant="outline" onClick={repairProvisioning}
                        disabled={busy === 'sample'} data-testid="retry-sample-btn">
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Retry Sample Data
                </Button>
              )}
            </div>

            {/* Remove Sample Data is deliberately absent. The current backend
                deletes every manifest-registered id unconditionally and then
                purges the batch from the Recycle Bin, so an adopted sample
                record takes its real children with it and nothing comes back.
                The preflighted safe-cleanup path is built; this action appears
                when it is exposed. */}
          </>
        )}
      </CardContent>

      {/* Confirm before contacting someone outside Cluvik. */}
      <Dialog open={confirmSend} onOpenChange={setConfirmSend}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {state === 'never_sent' ? 'Send invitation' : 'Send a new invitation'}
            </DialogTitle>
            <DialogDescription data-testid="invitation-confirm-text">
              {state === 'never_sent' ? (
                <>This emails <strong>{invitation.recipient}</strong> a link to set their password
                and sign in.</>
              ) : (
                <>An invitation was already sent to <strong>{invitation.recipient}</strong>
                {invitation.last_sent_at && <> on {fmt(invitation.last_sent_at)}</>}. Sending a new
                one issues a fresh link and <strong>immediately stops the previous one working</strong>.</>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmSend(false)}>Cancel</Button>
            <Button onClick={sendInvitation} disabled={busy === 'invitation'}>
              {busy === 'invitation' ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              {state === 'never_sent' ? 'Send Invitation' : 'Send New Invitation'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Shown once. Closing the dialog is the only copy leaving memory. */}
      <Dialog open={!!tempPassword} onOpenChange={() => { setTempPassword(null); setCopied(false); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Temporary password generated</DialogTitle>
            <DialogDescription>
              For <strong>{tempPassword?.email}</strong>. This is the only time it can be
              seen — it is stored as a hash and cannot be retrieved again.
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-center gap-2">
            <code className="flex-1 break-all rounded-lg border border-border bg-muted px-3 py-2 font-mono text-sm text-foreground"
                  data-testid="temp-password-value">
              {tempPassword?.temporary_password}
            </code>
            <Button size="sm" variant="outline" data-testid="copy-temp-password"
                    onClick={() => {
                      navigator.clipboard?.writeText(tempPassword.temporary_password);
                      setCopied(true);
                    }}>
              {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            Expires {fmt(tempPassword?.expires_at)}. The user must choose a new password when
            they sign in.
          </p>
          <DialogFooter>
            <Button onClick={() => { setTempPassword(null); setCopied(false); }}>
              I have copied it
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View As — the operator's reason, then startViewAs. */}
      <ImpersonationReasonDialog
        target={viewAsTarget}
        post={(path, body) => axios.post(`${CONTROL_PLANE_API}${path}`, body, { headers })}
        onClose={() => setViewAsTarget(null)}
      />
    </Card>
  );
};

export default OnboardingPanel;
